import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  DialogActions,
  useTheme,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Grid2,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import AppButton from "../../../../../../views/components/common/buttons/AppButton";
import {
  InvoicePayment,
  InvoicePaymentRequest,
  PaymentMode,
} from "../../../../../../models/Invoice/Invoice";
import FormTitle from "../../../../../../views/components/common/form/FormTitle";
import FormSmallTextField from "../../../../../../views/components/common/form/FormTextField";
import { useNotification } from "../../../../../../utils/NotificationsContext";
import { AdvancePayment } from "../../../../../../models/Order/AdvancePayment";

interface AddPaymentInfoProps {
  payment?: InvoicePayment;
  editAdvancePayment?: AdvancePayment;
  onSubmit: (payment: InvoicePaymentRequest) => void;
  onClose: () => void;
}

interface ChargeInputs {
  bank_charges: number | string;
  tds_charges: number | string;
  forex_gain: number | string;
  forex_loss: number | string;
  other_charges: number | string;
}

const EMPTY_CHARGE_INPUTS: ChargeInputs = {
  bank_charges: "",
  tds_charges: "",
  forex_gain: "",
  forex_loss: "",
  other_charges: "",
};

const FIXED_CHARGE_FIELDS: { key: keyof ChargeInputs; label: string }[] = [
  { key: "bank_charges", label: "Bank Charges" },
  { key: "tds_charges", label: "TDS Charges" },
  { key: "forex_gain", label: "Forex Gain" },
  { key: "forex_loss", label: "Forex Loss" },
  { key: "other_charges", label: "Other Charges" },
];

const buildChargeInputsFromAdvancePayment = (
  ap?: AdvancePayment,
): ChargeInputs => {
  if (!ap) return { ...EMPTY_CHARGE_INPUTS };

  return {
    bank_charges: ap.bank_charges || "",
    tds_charges: ap.tds_charges || "",
    forex_gain:
      ap.forex_charges != null && ap.forex_charges < 0
        ? Math.abs(ap.forex_charges)
        : "",
    forex_loss:
      ap.forex_charges != null && ap.forex_charges > 0 ? ap.forex_charges : "",
    other_charges: ap.other_charges || "",
  };
};

const aggregateCharges = (
  inputs: ChargeInputs,
): Pick<
  InvoicePaymentRequest,
  "bank_charges" | "tds_charges" | "forex_charges" | "other_charges"
> => {
  const bank_charges = Number(inputs.bank_charges) || 0;
  const tds_charges = Number(inputs.tds_charges) || 0;
  const other_charges = Number(inputs.other_charges) || 0;
  const forex_gain = Number(inputs.forex_gain) || 0;
  const forex_loss = Number(inputs.forex_loss) || 0;
  const forex_charges = forex_loss - forex_gain;

  return { bank_charges, tds_charges, forex_charges, other_charges };
};

type AggregatedCharges = Pick<
  InvoicePaymentRequest,
  "bank_charges" | "tds_charges" | "forex_charges" | "other_charges"
>;

const calcTotalAmount = (
  amountReceived: number,
  charges: AggregatedCharges,
): number =>
  amountReceived +
  (charges.bank_charges || 0) +
  (charges.tds_charges || 0) +
  (charges.other_charges || 0) +
  (charges.forex_charges || 0);

const getAmountReceivedFromAdvancePayment = (
  ap?: AdvancePayment,
): number => {
  if (!ap) return 0;
  return (
    (Number(ap.amount) || 0) -
    (ap.bank_charges || 0) -
    (ap.tds_charges || 0) -
    (ap.other_charges || 0) -
    (ap.forex_charges || 0)
  );
};

const getBasePaymentInfo = (
  editAdvancePayment?: AdvancePayment,
): InvoicePaymentRequest => ({
  payment_mode:
    (editAdvancePayment?.payment_mode as PaymentMode) ?? undefined,
  exchange_rate: 1,
  recorded_date: editAdvancePayment?.recorded_date
    ? new Date(editAdvancePayment.recorded_date).toISOString().split("T")[0]
    : new Date().toISOString().split("T")[0],
});

const numberInputSx = {
  "& input[type=number]": {
    "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
      display: "none",
    },
    MozAppearance: "textfield",
  },
};

export default function AddPaymentInfoPopup(props: AddPaymentInfoProps) {
  const { onClose, editAdvancePayment } = props;
  const { showError } = useNotification();
  const theme = useTheme();

  const [paymentInfo, setPaymentInfo] = useState<InvoicePaymentRequest>(() =>
    getBasePaymentInfo(editAdvancePayment),
  );
  const [amountReceived, setAmountReceived] = useState<number | string>(() =>
    getAmountReceivedFromAdvancePayment(editAdvancePayment) || "",
  );
  const [chargeInputs, setChargeInputs] = useState<ChargeInputs>(() =>
    buildChargeInputsFromAdvancePayment(editAdvancePayment),
  );

  const aggregatedCharges = useMemo(
    () => aggregateCharges(chargeInputs),
    [chargeInputs],
  );

  const calculatedTotalAmount = useMemo(
    () => calcTotalAmount(Number(amountReceived) || 0, aggregatedCharges),
    [amountReceived, aggregatedCharges],
  );

  useEffect(() => {
    setPaymentInfo(getBasePaymentInfo(editAdvancePayment));
    setAmountReceived(
      getAmountReceivedFromAdvancePayment(editAdvancePayment) || "",
    );
    setChargeInputs(buildChargeInputsFromAdvancePayment(editAdvancePayment));
  }, [editAdvancePayment]);

  const handleClearAll = () => {
    if (editAdvancePayment) {
      setPaymentInfo(getBasePaymentInfo(editAdvancePayment));
      setAmountReceived(
        getAmountReceivedFromAdvancePayment(editAdvancePayment) || "",
      );
      setChargeInputs(buildChargeInputsFromAdvancePayment(editAdvancePayment));
    } else {
      setPaymentInfo(getBasePaymentInfo());
      setAmountReceived("");
      setChargeInputs({ ...EMPTY_CHARGE_INPUTS });
    }
  };

  const updatePaymentInfo = (name: string, value: any) => {
    setPaymentInfo((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const updateChargeInput = (key: keyof ChargeInputs, value: string) => {
    setChargeInputs((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleSubmit = () => {
    if (!paymentInfo.payment_mode) {
      showError("Error Occurred", "Please select payment mode");
      return;
    } else if (!amountReceived || Number(amountReceived) === 0) {
      showError("Error Occurred", "Please enter amount received");
      return;
    } else if (paymentInfo.recorded_date === "") {
      showError("Error Occurred", "Please select date");
      return;
    } else if (calculatedTotalAmount <= 0) {
      showError(
        "Error Occurred",
        "Calculated total amount must be greater than zero.",
      );
      return;
    }

    props.onSubmit({
      ...paymentInfo,
      amount: calculatedTotalAmount,
      ...aggregatedCharges,
    });
  };

  return (
    <Dialog open fullWidth maxWidth="sm">
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          paddingX: 5,
          paddingBottom: 1,
          paddingTop: 3.5,
        }}
      >
        <CustomTypography variant="heading4" fontWeight={700}>
          {editAdvancePayment ? "Edit Advance Payment" : "Add Payment"}
        </CustomTypography>
        <IconButton
          sx={{
            color: "black",
            "&:hover": {
              backgroundColor: "grey",
            },
          }}
          size="small"
          onClick={onClose}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <DialogContent sx={{ paddingX: 5, paddingTop: 0 }}>
        <Box
          sx={{
            mt: 3,
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "minmax(0, 1fr) minmax(0, 1fr)",
            },
            columnGap: { xs: 2, md: 4 },
            rowGap: 2,
            width: "100%",
          }}
        >
          <Box>
            <FormTitle
              title="Payment Mode"
              color={theme.palette.customColors.greyTextColor}
              fontWeight="500"
            />
            <FormControl fullWidth size="small">
              <InputLabel id="payment term" sx={{ fontSize: "0.875rem" }}>
                Select Mode*
              </InputLabel>
              <Select
                label="Select payment term"
                onChange={(event: SelectChangeEvent<"">) =>
                  updatePaymentInfo("payment_mode", event.target.value)
                }
                sx={{ height: "2.375rem" }}
                value={paymentInfo.payment_mode}
                name="payment_mode"
                required
              >
                {Object.entries(PaymentMode).map(([key, value]) => (
                  <MenuItem key={key} value={value}>
                    {key}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box>
            <FormTitle
              title="Amount Received"
              color={theme.palette.customColors.greyTextColor}
              fontWeight="500"
            />
            <FormSmallTextField
              label="Enter amount received"
              variant="outlined"
              fontSize="0.875rem"
              marginBottom={0}
              id="amount_received"
              onChange={(id, value) => setAmountReceived(value ?? "")}
              required
              value={amountReceived}
              type="number"
              sx={numberInputSx}
            />
          </Box>

          {FIXED_CHARGE_FIELDS.map(({ key, label }) => (
            <Box key={key}>
              <FormTitle
                title={label}
                color={theme.palette.customColors.greyTextColor}
                fontWeight="500"
              />
              <FormSmallTextField
                label={`Enter ${label.toLowerCase()}`}
                variant="outlined"
                fontSize="0.875rem"
                marginBottom={0}
                id={key}
                onChange={(id, value) => updateChargeInput(key, value ?? "")}
                value={chargeInputs[key]}
                type="number"
                sx={numberInputSx}
              />
            </Box>
          ))}

          <Box>
            <FormTitle
              title="Date*"
              color={theme.palette.customColors.greyTextColor}
              fontWeight="500"
            />
            <FormSmallTextField
              label=""
              variant="outlined"
              fontSize="0.875rem"
              marginBottom={0}
              id="recorded_date"
              onChange={(id, value) => updatePaymentInfo("recorded_date", value)}
              type="date"
              required
              value={paymentInfo.recorded_date}
            />
          </Box>

          <Box
            sx={{
              gridColumn: { xs: "1", md: "span 2" },
              px: 0.5,
              py: 1.25,
              borderRadius: 1,
              backgroundColor: theme.palette.customColors.greyBodyBackground,
            }}
          >
            <CustomTypography
              variant="body3"
              color={theme.palette.customColors.greyTextColor}
              fontWeight={500}
            >
              Total Amount (calculated)
            </CustomTypography>
            <CustomTypography variant="body2" fontWeight={700}>
              {calculatedTotalAmount.toFixed(2)}
            </CustomTypography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          display: "block",
          paddingLeft: 4.5,
          paddingRight: 4.5,
        }}
      >
        <Grid2 container columnSpacing={4}>
          <Grid2 size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}>
            <AppButton
              label="Save Details"
              onClick={handleSubmit}
              variant="primaryBlock"
              customStyles={{
                padding: 3,
                paddingTop: 1,
                paddingBottom: 1,
                width: "100%",
                margin: 0,
                fontWeight: 500,
                fontSize: "1rem",
                marginBottom: "1rem",
              }}
            />
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}>
            <AppButton
              label="Clear All"
              onClick={handleClearAll}
              variant="grayscale"
              customStyles={{
                padding: 8.5,
                paddingTop: 1,
                width: "100%",
                paddingBottom: 1,
                margin: 0,
                fontWeight: 600,
                fontSize: "1rem",
                marginBottom: "1rem",
              }}
            />
          </Grid2>
        </Grid2>
      </DialogActions>
    </Dialog>
  );
}
